"use client"

import type React from "react"

import { cn } from "@/lib/utils"
import { ArrowUpRight } from "lucide-react"
import { FF_CITATIONS_PLACEHOLDER_MODE } from "@/lib/config/feature-flags"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface ExternalSourceLinkProps {
  /** URL to link to */
  url?: string
  /** Alternative: use href (for backwards compatibility) */
  href?: string
  /** Display label */
  label?: string
  /** Alternative: use children (for backwards compatibility) */
  children?: React.ReactNode
  /** Controls link color based on surrounding text context */
  intensity?: "muted" | "strong"
  /** Force placeholder mode (for testing) */
  forcePlaceholder?: boolean
  className?: string
}

/**
 * ExternalSourceLink - Inline link for external website sources
 *
 * Renders as underlined link with external-arrow icon (45° up-right).
 * - Opens in new tab with noopener/noreferrer
 * - Uses muted accent when in muted paragraph, primary accent otherwise
 * - Hover: underline thickness change + subtle icon motion (no layout shift)
 * - Placeholder mode: renders as span with tooltip "Placeholder (design mode)"
 */
export function ExternalSourceLink({
  url,
  href,
  label,
  children,
  intensity = "strong",
  forcePlaceholder,
  className,
}: ExternalSourceLinkProps) {
  const linkUrl = url || href || "#"
  const linkLabel = label || children

  const isPlaceholder = forcePlaceholder || FF_CITATIONS_PLACEHOLDER_MODE

  const linkClassName = cn(
    "inline-flex items-center gap-0.5 underline decoration-1 underline-offset-2",
    "transition-all duration-150",
    // Color based on intensity
    intensity === "muted"
      ? "text-muted-foreground hover:text-foreground decoration-muted-foreground/50 hover:decoration-muted-foreground"
      : "text-primary hover:text-primary/80 decoration-primary/50 hover:decoration-primary",
    // Hover: thicker underline, no layout shift
    "hover:decoration-2",
    // Placeholder mode: disabled cursor
    isPlaceholder && "cursor-default",
    className,
  )

  const linkContent = isPlaceholder ? (
    <span className={linkClassName}>
      <span>{linkLabel}</span>
      <ArrowUpRight className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden="true" />
    </span>
  ) : (
    <a href={linkUrl} target="_blank" rel="noopener noreferrer" className={linkClassName}>
      <span>{linkLabel}</span>
      <ArrowUpRight
        className={cn(
          "h-3 w-3 shrink-0 transition-transform duration-150",
          // Subtle icon motion on hover (≤2px)
          "group-hover:translate-x-px group-hover:-translate-y-px",
        )}
        strokeWidth={2}
        aria-hidden="true"
      />
    </a>
  )

  if (isPlaceholder) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
          <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-slate-200 text-xs">
            Placeholder (design mode)
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return linkContent
}
